/**
 * Validated environment configuration.
 *
 * All env vars are validated at module load time — the app will fail fast
 * if required variables are missing in production.
 *
 * ## Adding a new env var
 *
 * 1. Add the raw var to `src/vite-env.d.ts`       (TypeScript type)
 * 2. Add the parsed field to `AppEnv` below        (typed interface)
 * 3. Add the reader in the `env` object below      (validated read — use
 *    `getRequiredEnv` for vars that must be present in production, or
 *    `getOptionalEnv` for vars whose absence is a valid state)
 * 4. Add to `.env.example`                         (documentation + classification)
 * 5. Add to `.env`                                 (local dev value)
 * 6. Update tests in `env.spec.ts`                 (shape assertion)
 */

import { getOptionalEnv, getRequiredEnv } from '@klareospace/pharos-lib-web-utils';

export interface AppEnv {
  /** Base URL for API requests (no trailing slash) */
  apiBaseUrl: string;
  /** Azure Application Insights connection string. Empty string disables telemetry. */
  appInsightsConnectionString: string;
  /** Firebase API key */
  firebaseApiKey: string;
  /** Firebase App ID */
  firebaseAppId: string;
  /** Firebase Auth Domain */
  firebaseAuthDomain: string;
  /** Firebase Messaging Sender ID */
  firebaseMessagingSenderId: string;
  /** Firebase Project ID */
  firebaseProjectId: string;
  /** Firebase Storage Bucket */
  firebaseStorageBucket: string;
  /** HERE Maps API key (used for map tiles) */
  hereApiKey: string;
  /** Whether the app is running in development mode */
  isDev: boolean;
  /** Whether the app is running in production mode */
  isProd: boolean;
  /** Pendo web agent API key. Empty string disables Pendo. */
  pendoApiKey: string;
}

export const env: AppEnv = {
  apiBaseUrl: getRequiredEnv('VITE_API_BASE_URL').replace(/\/$/, ''),
  // NON-SECRET: empty string is the documented "telemetry disabled" state — use getOptionalEnv.
  appInsightsConnectionString: getOptionalEnv('VITE_APPINSIGHTS_CONNECTION_STRING'),
  firebaseApiKey: getRequiredEnv('VITE_FIREBASE_API_KEY'),
  firebaseAppId: getRequiredEnv('VITE_FIREBASE_APP_ID'),
  firebaseAuthDomain: getRequiredEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  firebaseMessagingSenderId: getRequiredEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  firebaseProjectId: getRequiredEnv('VITE_FIREBASE_PROJECT_ID'),
  firebaseStorageBucket: getRequiredEnv('VITE_FIREBASE_STORAGE_BUCKET'),
  hereApiKey: getRequiredEnv('VITE_HERE_API_KEY'),
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  // NON-SECRET: empty string is the documented "Pendo disabled" state — use getOptionalEnv, never getRequiredEnv.
  pendoApiKey: getOptionalEnv('VITE_PENDO_API_KEY'),
};
